/**
 * Распределить нераспределённый остаток.
 *
 * Экран отвечает на вопрос «эти деньги ничему не обещаны — что с ними
 * сделать», и главное здесь не список направлений, а **разница в
 * последствиях**, которую человек обязан видеть до нажатия:
 *
 * · перевод в корзину меняет состав доступных денег, но не их сумму: деньги
 *   как были в его распоряжении, так и остались;
 * · перевод в цель или под платёж уменьшает «Доступно»: они обещаны
 *   другому;
 * · назначить деньги на долг — не то же самое, что заплатить по нему: долг
 *   остаётся, появляется конверт.
 *
 * Все числа считает ядро (`distributeRest`), и показанное «станет» — ровно
 * то, что запишется: предпросмотр, посчитанный отдельно от записи, рано или
 * поздно разойдётся с ней.
 *
 * Пункта «оставить доступными» здесь нет: всё, чего человек не назначил, и
 * так остаётся нераспределённым.
 */
import { useState } from 'react';
import {
  ZERO, activeAllocation, add, clampPos, distributeRest, formatMoney, sub,
  type Money, type RestTarget, type RestTargetKind,
} from '@skoka/core';
import { SheetWrap } from './sheet.js';
import { Tip } from './tip.js';
import { flash } from './flash.js';
import { AmountInput } from './amount.js';
import {
  allocationViewOf, applyRest, formatAmountInput, parseAmountInput, type AppState,
} from './store.js';

interface Row {
  key: string;
  kind: RestTargetKind;
  id?: string;
  title: string;
  note: string;
  /** Уменьшает ли это «Доступно»: конверт — да, корзина — нет. */
  binds: boolean;
}

export function DistributeRest({ state, onClose, onDone }: {
  state: AppState;
  onClose: () => void;
  onDone: () => Promise<void> | void;
}) {
  const allocation = activeAllocation(state.allocations);
  const view = allocation ? allocationViewOf(state, allocation) : null;
  const [amounts, setAmounts] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');

  if (!allocation || !view) {
    return (
      <SheetWrap title="Распределить" onClose={onClose}>
        <div className="note" style={{ marginTop: 0 }}>
          Деньги ещё не разложены — распределять нечего.
        </div>
      </SheetWrap>
    );
  }

  const goals = state.goals.filter((g) => g.status === 'active');
  const buffer = goals.find((g) => g.kind === 'emergency_fund');
  const debts = state.obligations.filter((o) => o.isActive).slice(0, 6);

  const rows: Row[] = [
    {
      key: 'need', kind: 'need', title: 'Увеличить повседневные нужды',
      note: 'останутся доступными, просто с назначением', binds: false,
    },
    {
      key: 'want', kind: 'want', title: 'Увеличить свободные траты',
      note: 'останутся доступными, просто с назначением', binds: false,
    },
    ...(buffer
      ? [{
        key: `goal:${buffer.id}`, kind: 'goal' as const, id: buffer.id,
        title: 'В подушку безопасности',
        note: `сейчас ${formatMoney(buffer.currentAmount)} — деньги станут недоступными`,
        binds: true,
      }]
      : []),
    ...goals.filter((g) => g.kind !== 'emergency_fund').map((g) => ({
      key: `goal:${g.id}`, kind: 'goal' as const, id: g.id,
      title: `В цель «${g.name}»`,
      note: `собрано ${formatMoney(g.currentAmount)} из ${formatMoney(g.targetAmount)}`,
      binds: true,
    })),
    ...debts.map((o) => ({
      key: `obligation:${o.id}`, kind: 'obligation' as const, id: o.id,
      title: `На платёж «${o.name}»`,
      note: 'деньги закрепятся под платёж — сам он от этого не становится оплаченным',
      binds: true,
    })),
  ];

  const targets: RestTarget[] = rows
    .map((r) => ({
      kind: r.kind,
      ...(r.id ? { id: r.id } : {}),
      amount: parseAmountInput(amounts[r.key] ?? ''),
    }))
    .filter((t) => t.amount > ZERO);

  const plan = distributeRest({
    allocation,
    current: {
      need: view.needLeft,
      want: view.wantLeft,
      carry: view.carry,
      unallocated: view.unallocated,
      owed: view.owedBy,
    },
    liquid: view.liquid,
    reserved: view.reserved,
    // Что уже лежит в конвертах — как при записи. Без этого предпросмотр
    // показывал «0 → X» у конверта, где деньги уже были.
    envelopes: Object.fromEntries(state.reserves.reduce((m, r) => {
      const key = `${r.forKind}:${r.forId}`;
      m.set(key, add(m.get(key) ?? ZERO, r.amount));
      return m;
    }, new Map<string, Money>())),
    targets,
  });
  const rest = view.unallocated;
  const over = plan.assigned > rest;

  return (
    <SheetWrap title={`Распределить ${formatMoney(rest)}`} onClose={onClose}>
      {/* У каждого поля своё имя: девять одинаковых «Сумма» диктор не различал. */}
      {rows.map((r) => (
        <div className="li fund" key={r.key}>
          <div className="nm"><b>{r.title}</b><span>{r.note}</span></div>
          <div className="fundside">
            <AmountInput value={amounts[r.key] ?? ''} placeholder="0 ₽" className="fundamt"
              label={`Сумма: ${r.title}`}
              onChange={(v) => setAmounts((a) => ({ ...a, [r.key]: v }))} />
          </div>
        </div>
      ))}

      {over && (
        <div className="note warn">
          Больше, чем есть: нераспределённых денег {formatMoney(rest)}. Уменьши
          какую-нибудь строку — от завышенного числа денег не прибавится.
        </div>
      )}

      {/*
        Последствия — до применения и теми же числами, что запишутся. Строки
        с нулём не показываются: они ничего не сообщают.
      */}
      {plan.ok && plan.assigned > ZERO && (
        <section className="blk">
          <div className="blkhead"><b>После применения
            <Tip>{plan.add.length > 0
              ? 'Деньги в целях и под платежами перестают быть доступными: они обещаны.'
              : 'Сумма «Не закреплено» не изменится: деньги остаются твоими, меняется только назначение.'}</Tip></b></div>
          <Change label="Не закреплено" before={plan.before.available} after={plan.after.available} />
          {plan.after.need !== plan.before.need && (
            <Change label="Повседневные нужды" before={plan.before.need} after={plan.after.need} />
          )}
          {plan.after.want !== plan.before.want && (
            <Change label="Свободные траты" before={plan.before.want} after={plan.after.want} />
          )}
          <Change label="Пока не распределено"
            before={plan.before.unallocated} after={plan.after.unallocated} />
          {plan.after.envelopes.map((e, i) => (
            <Change key={e.id}
              label={nameOf(state, e.kind, e.id)}
              before={plan.before.envelopes[i]?.amount ?? ZERO}
              after={e.amount} />
          ))}
        </section>
      )}

      {problem && <div className="note warn">{problem}</div>}

      <button className="btn" onClick={onClose}>Оставить как есть</button>

      {/* Остаток и действие — рядом и всегда на виду, а не в разных концах
          длинного списка. */}
      <div className="impbar">
        <div className="row"><div className="rl">Назначено {formatMoney(plan.assigned)} из {formatMoney(rest)}</div>
          <div className="rv num">останется {formatMoney(clampPos(sub(rest, plan.assigned)))}</div></div>
        <button className="btn pri" disabled={busy || over || plan.assigned <= ZERO}
          onClick={async () => {
            setBusy(true);
            const r = await applyRest({ allocationId: allocation.id, targets });
            setBusy(false);
            if (!r.done) { setProblem(r.reason ?? 'Не вышло'); return; }
            flash('Остаток распределён');
            await onDone();
          }}>
          {busy ? 'Применяю…' : 'Применить'}
        </button>
      </div>
    </SheetWrap>
  );
}

const Change = ({ label, before, after }: { label: string; before: Money; after: Money }) => (
  <div className="row">
    <div className="rl">{label}</div>
    <div className="rv num">{formatMoney(before)} → {formatMoney(after)}</div>
  </div>
);

/** Имя конверта словами человека: цель или платёж, а не идентификатор. */
function nameOf(state: AppState, kind: 'goal' | 'obligation' | string, id: string): string {
  if (kind === 'goal') {
    const g = state.goals.find((x) => x.id === id);
    return g ? (g.kind === 'emergency_fund' ? 'Подушка безопасности' : `Цель «${g.name}»`) : 'Цель';
  }
  return `Платёж «${state.obligations.find((x) => x.id === id)?.name ?? '—'}»`;
}

/** Сколько сейчас нераспределено: для карточки на главной. */
export const restOf = (state: AppState): Money => {
  const a = activeAllocation(state.allocations);
  return a ? add(a.lines.unallocated) : ZERO;
};
